import {
  NewColorID,
  PaletteColorToTransit,
  type ColorID,
  type PaletteColor,
} from '@/components/ColorPreview';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { useAuthentication, type SSEEventHandler } from './AuthService';
import {
  MakeInitialTypographyConversationItems,
  type TextDetails,
  type TypographyID,
  type TypographyStyle,
} from '@/components/TypographyPreview';
import {
  MakeInitialScreenConversationItems,
  type ScreenID,
} from '@/components/ScreenPreview';
import { NewVersionID, type VersionID } from '@/components/VersionsPreview';
import { EnableCleanSlate } from '@/components/CleanSlate';
import type {
  Component,
  ComponentID,
  FunctionComponentID,
} from '@/components/ComponentsPreview';

export type Sender = 'user' | 'system';

export type ConversationMessage = {
  content: string;
  sender: Sender;
};

export type FidgetModule =
  | 'colors'
  | 'typography'
  | 'screens'
  | 'components'
  | 'connect'
  | 'launch';
export const ALL_FIDGET_MODULES: FidgetModule[] = [
  'colors',
  'typography',
  'screens',
  'connect',
  'launch',
];

export type ConversationEvent =
  | {
      type: 'started';
      conversationID: string;
    }
  | {
      type: 'item';
      conversationItem: ConversationItem;
    }
  | { type: 'fidget'; fidgetModule: FidgetModule };

export type TransitColor =
  | { type: 'lightOnly'; lightColorHex: string }
  | { type: 'lightAndDark'; lightColorHex: string; darkColorHex: string };

export type SpecificConversationItem<Type> = Omit<
  Extract<ConversationItem, { type: Type }>,
  'type'
>;

export type UpdateColorPaletteAddition =
  SpecificConversationItem<'defineColor'>;
export type UpdateColorPaletteRemoval = { name: string } | string;
export type UpdateColorPaletteItem = {
  type: 'updateColorPalette';
  additions: UpdateColorPaletteAddition[];
  removals: UpdateColorPaletteRemoval[];
  suggestion: unknown;
};

export type UpdateTextStyleAddition =
  SpecificConversationItem<'defineTextStyle'>;
export type UpdateTextStyleRemoval = { name: string } | string;
export type UpdateTextStylesItem = {
  type: 'updateTextStyles';
  additions: UpdateTextStyleAddition[];
  removals: UpdateTextStyleRemoval[];
};

export type ColorValue =
  | {
      type: 'value';
      value: {
        colorHex: string;
      };
    }
  | {
      type: 'reference';
      reference: string;
    };
export type ComponentPropertyValue =
  | {
      type: 'color';
      value: ColorValue | null;
    }
  | {
      type: 'string';
      value: string | null;
    }
  | {
      type: 'bool';
      value: boolean | null;
    }
  | {
      type: 'int';
      value: number | null;
    }
  | {
      type: 'dictionary';
      value: Record<string, ComponentPropertyValue> | null;
    }
  | {
      type: 'array';
      value: ComponentPropertyValue[] | null;
    };

export type ComponentProperty = {
  name: string;
  value: ComponentPropertyValue;
};

type Binding = {
  propertyName: string;
  componentID?: ComponentID;
};

export type FunctionParameter = {
  name: string;
  binding: Binding;
  defaultValue?: ComponentPropertyValue;
};

type ComponentParent =
  | {
      type: 'component';
      id: ComponentID;
    }
  | {
      type: 'screen';
      id: ScreenID;
    };

// TODO(LAB-261): Add support for `updateScreenManifest`
export type ConversationItem =
  | { type: 'defineColor'; id: ColorID; name: string; color: TransitColor }
  | {
      type: 'removeColor';
      name: string;
    }
  | UpdateColorPaletteItem
  | UpdateTextStylesItem
  | ({ type: 'message' } & ConversationMessage)
  | {
      type: 'defineTextStyle';
      name: string;
      style: TextDetails;
      id: TypographyID;
    }
  | { type: 'removeTextStyle'; name: string }
  | {
      type: 'addScreen';
      name: string;
      id: string;
    }
  | { type: 'removeScreen'; id: string }
  | {
      type: 'writeProperties';
      componentID: ComponentID;
      properties: ComponentProperty[];
    }
  | {
      type: 'defineComponentStyle';
      id: string;
      name: string;
      properties: ComponentProperty[];
    }
  | { type: 'removeComponentStyle'; name: string }
  | {
      type: 'declareFunctionComponent';
      id: FunctionComponentID;
      name: string;
      templateID: ComponentID;
      parameters: FunctionParameter[];
    }
  | {
      type: 'removeFunctionComponent';
      name: string;
    }
  | {
      type: 'addChildComponent';
      childID: ComponentID;
      parent: ComponentParent;
    }
  | {
      type: 'createComponent';
      id: ComponentID;
      componentType: Component['type'];
    }
  | ExperimentalConversationItem;

export type ExperimentalConversationItem =
  | { type: 'describeScreen'; description: string; id: string }
  | { type: 'connect'; name: string; id: ScreenID; to: ScreenID }
  | { type: 'createVersion'; id: VersionID; name: string };

export function CodiMessage({
  content,
}: {
  content: string;
}): ConversationItem {
  return { type: 'message', content, sender: 'system' };
}

export function UserMessage({
  content,
}: {
  content: string;
}): ConversationItem {
  return { type: 'message', content, sender: 'user' };
}

type ConversationService = {
  sendItems: (_: { newItems: ConversationItem[]; stream?: boolean }) => void;
  startNewConversation: () => void;
  sharePublicly: () => void;
};

export const ConversationSubmitErrorCopy =
  'Unable to send your message right now. Please try again.';
export const ConversationStreamInterruptedCopy =
  'Response was interrupted. Partial output is shown. Please retry.';

export type ConversationAction =
  | { type: 'start-load' }
  | { type: 'fail-load' }
  | { type: 'submit-failed'; errorMessage?: string }
  | { type: 'finish-load' }
  | { type: 'interrupt-stream'; errorMessage?: string }
  | {
      type: 'hydrate-conversation';
      conversationID: string;
      items: ConversationItem[];
      sharedToPublic?: boolean;
    }
  | {
      type: 'received-conversation';
      conversationID: string;
      items: ConversationItem[];
      sharedToPublic?: boolean;
    }
  | {
      type: 'promote-to-conversation';
      conversationID: string;
      extraItems?: ConversationItem[];
      sharedToPublic?: boolean;
      clearDraftItems?: boolean;
    }
  | {
      type: 'update-share-status';
      conversationID: string;
      sharedToPublic: boolean;
    }
  | { type: 'reset-conversation' }
  | { type: 'commit-items'; items: ConversationItem[] }
  | { type: 'rollback-items'; count: number }
  | { type: 'add-color'; color: PaletteColor }
  | { type: 'remove-color'; name: string }
  | { type: 'change-color'; color: PaletteColor; priorName: string }
  | { type: 'add-typography-style'; typographyStyle: TypographyStyle }
  | { type: 'remove-typography-style'; name: string }
  | {
      type: 'change-typography-style';
      typographyStyle: TypographyStyle;
      priorName: string;
    }
  | { type: 'add-drafts'; draftItems: ConversationItem[] }
  | { type: 'start-stream' }
  | { type: 'complete-stream' }
  | { type: 'stream-item'; item: ConversationItem }
  | { type: 'stream-conversation-id'; conversationID: string }
  | { type: 'stream-fidget'; module: FidgetModule };

export type ConversationDispatch = (_: ConversationAction) => void;

export type StreamingState = {
  conversationID?: string;
  items: ConversationItem[];
  availableFidgetModules: FidgetModule[];
};

export type LoadingState = {
  streaming?: StreamingState;
};

type ConversationStateShared = {
  items: ConversationItem[];
  draftItems: ConversationItem[];
  loading?: LoadingState;
  interruptedItems: ConversationItem[];
  errorMessage: string | null;
};

export type ConversationState =
  | ({
      type: 'new';
    } & ConversationStateShared)
  | ({
      type: 'conversation';
      conversationID: string;
      sharedToPublic?: boolean;
    } & ConversationStateShared);

type ServerConversation = {
  id: string;
  items: ConversationItem[];
  sharedToPublic?: boolean;
};

export type ConversationContextProps = {
  service: ConversationService;
  state: ConversationState;
  dispatch: ConversationDispatch;
};
export const ConversationContext =
  createContext<ConversationContextProps | null>(null);
export function useConversation(): ConversationContextProps {
  const context = useContext(ConversationContext);
  if (context === null) {
    throw new Error(
      'useConversation must be used within a ConversationBoundary',
    );
  }

  return context;
}

function isKnownConversationItem(
  item: ConversationItem | { type: unknown } | undefined,
): boolean {
  if (item === undefined) {
    return false;
  }

  switch (item.type) {
    case 'defineColor':
    case 'removeColor':
    case 'updateColorPalette':
    case 'updateTextStyles':
    case 'message':
    case 'defineTextStyle':
    case 'removeTextStyle':
    case 'addScreen':
    case 'removeScreen':
    case 'describeScreen':
    case 'connect':
    case 'writeProperties':
    case 'defineComponentStyle':
    case 'removeComponentStyle':
    case 'declareFunctionComponent':
    case 'removeFunctionComponent':
    case 'createComponent':
    case 'addChildComponent':
    case 'createVersion':
      return true;
  }

  return false;
}

export function useAllItems({
  activeVersionID,
}: {
  activeVersionID?: string;
}): ConversationItem[] {
  const { state } = useConversation();

  const allItems = useMemo(() => {
    const streamingItems = state.loading?.streaming?.items ?? [];
    const interruptedItems = state.interruptedItems;
    const allItems = state.items
      .concat(state.draftItems)
      .concat(streamingItems)
      .concat(interruptedItems);

    if (!activeVersionID) {
      return allItems;
    }

    const cutoff = allItems.findIndex((item) => {
      return item.type === 'createVersion' && item.id === activeVersionID;
    });

    return cutoff === -1 ? allItems : allItems.slice(0, cutoff);
  }, [state, activeVersionID]);

  return allItems.filter((item) => isKnownConversationItem(item));
}

function defineColorAction({
  id,
  name,
  color,
}: {
  id: ColorID;
  name: string;
  color: TransitColor;
}): ConversationItem {
  return { type: 'defineColor', id, name, color };
}

function defineTextStyleAction({
  id,
  name,
  style,
}: {
  id: TypographyID;
  name: string;
  style: TextDetails;
}): ConversationItem {
  return { type: 'defineTextStyle', id, name, style };
}

export function replaceOrAppendItem<T>({
  items,
  predicate,
  newItem,
}: {
  items: T[];
  predicate: (item: T) => boolean;
  newItem: T;
}) {
  const index = items.findIndex(predicate);

  if (index !== -1) {
    return [...items.slice(0, index), newItem, ...items.slice(index + 1)];
  }

  return items.concat(newItem);
}

export function conversationActionToImmediateItems(
  action: ConversationAction,
): ConversationItem[] | undefined {
  switch (action.type) {
    case 'add-color':
      return [
        defineColorAction({
          id: action.color.id,
          name: action.color.name,
          color: PaletteColorToTransit(action.color),
        }),
      ];
    case 'remove-color':
      return [{ type: 'removeColor', name: action.name }];
    case 'change-color':
      return action.priorName === action.color.name
        ? [
            defineColorAction({
              id: action.color.id,
              name: action.color.name,
              color: PaletteColorToTransit(action.color),
            }),
          ]
        : [
            { type: 'removeColor', name: action.priorName },
            defineColorAction({
              id: action.color.id,
              name: action.color.name,
              color: PaletteColorToTransit(action.color),
            }),
          ];
    case 'add-typography-style':
      return [
        defineTextStyleAction({
          id: action.typographyStyle.id,
          name: action.typographyStyle.name,
          style: action.typographyStyle.details,
        }),
      ];
    case 'remove-typography-style':
      return [{ type: 'removeTextStyle', name: action.name }];
    case 'change-typography-style':
      return action.priorName === action.typographyStyle.name
        ? [
            defineTextStyleAction({
              id: action.typographyStyle.id,
              name: action.typographyStyle.name,
              style: action.typographyStyle.details,
            }),
          ]
        : [
            { type: 'removeTextStyle', name: action.priorName },
            defineTextStyleAction({
              id: action.typographyStyle.id,
              name: action.typographyStyle.name,
              style: action.typographyStyle.details,
            }),
          ];
    default:
      return undefined;
  }
}

export function makeInitialDraftItems(): ConversationItem[] {
  return [
    CodiMessage({
      content:
        "Hi! I'm Codi. I’ll help you choose a color palette that suits your app perfectly.",
    }),
    CodiMessage({
      content:
        'Add your colors in this chat and I’ll connect them to the palette for you.',
    }),
    defineColorAction({
      id: NewColorID(),
      name: 'action.primary',
      color: { type: 'lightOnly', lightColorHex: '#d6e2fb' },
    }),
    defineColorAction({
      id: NewColorID(),
      name: 'text.primary',
      color: { type: 'lightOnly', lightColorHex: '#454545' },
    }),
    defineColorAction({
      id: NewColorID(),
      name: 'background.primary',
      color: { type: 'lightOnly', lightColorHex: '#FFFFFF' },
    }),
    defineColorAction({
      id: NewColorID(),
      name: 'background.secondary',
      color: { type: 'lightOnly', lightColorHex: '#EAEEF5' },
    }),
    {
      type: 'createVersion',
      name: 'Colourful beginnings!',
      id: NewVersionID(),
    },
    ...MakeInitialTypographyConversationItems(),
    ...MakeInitialScreenConversationItems(),
  ];
}

function createInitialConversationState(): ConversationState {
  if (EnableCleanSlate) {
    return {
      type: 'new',
      items: [],
      draftItems: [],
      loading: undefined,
      interruptedItems: [],
      errorMessage: null,
    };
  }

  return {
    type: 'new',
    items: makeInitialDraftItems(),
    draftItems: [],
    loading: undefined,
    interruptedItems: [],
    errorMessage: null,
  };
}

export function conversationReducer(
  state: ConversationState,
  action: ConversationAction,
): ConversationState {
  switch (action.type) {
    case 'start-load':
      return {
        ...state,
        loading: {
          streaming: undefined,
        },
        interruptedItems: [],
        errorMessage: null,
      };
    case 'fail-load':
      return { ...state, loading: undefined };
    case 'submit-failed':
      return {
        ...state,
        loading: undefined,
        errorMessage: action.errorMessage ?? ConversationSubmitErrorCopy,
      };
    case 'finish-load':
      return { ...state, loading: undefined };
    case 'interrupt-stream': {
      const partialStreamItems = state.loading?.streaming?.items ?? [];
      const startedConversationID = state.loading?.streaming?.conversationID;
      const errorMessage =
        action.errorMessage ?? ConversationStreamInterruptedCopy;

      if (state.type === 'new' && startedConversationID !== undefined) {
        return {
          type: 'conversation',
          items: state.items,
          conversationID: startedConversationID,
          draftItems: state.draftItems,
          loading: undefined,
          interruptedItems: partialStreamItems,
          errorMessage,
        };
      }

      return {
        ...state,
        loading: undefined,
        interruptedItems: partialStreamItems,
        errorMessage,
      };
    }
    case 'reset-conversation':
      return createInitialConversationState();
    case 'hydrate-conversation':
      return {
        type: 'conversation',
        items: action.items,
        draftItems: [],
        conversationID: action.conversationID,
        loading: undefined,
        interruptedItems: [],
        errorMessage: null,
        sharedToPublic: action.sharedToPublic,
      };
    case 'received-conversation': {
      return {
        type: 'conversation',
        items: state.items.concat(action.items),
        draftItems: [],
        conversationID: action.conversationID,
        loading: undefined,
        interruptedItems: [],
        errorMessage: null,
        sharedToPublic: action.sharedToPublic,
      };
    }
    case 'promote-to-conversation': {
      const clearDraftItems = action.clearDraftItems === true;
      const items = clearDraftItems
        ? state.items.concat(state.draftItems)
        : state.items;
      return {
        type: 'conversation',
        items: items.concat(action.extraItems ?? []),
        draftItems: clearDraftItems ? [] : state.draftItems,
        conversationID: action.conversationID,
        loading: undefined,
        interruptedItems: [],
        errorMessage: null,
        sharedToPublic: action.sharedToPublic,
      };
    }
    case 'update-share-status': {
      if (state.type !== 'conversation') {
        return state;
      }

      if (state.conversationID !== action.conversationID) {
        return state;
      }

      return {
        ...state,
        sharedToPublic: action.sharedToPublic,
        loading: undefined,
      };
    }
    case 'commit-items':
      return {
        ...state,
        items: state.items.concat(action.items),
      };
    case 'rollback-items':
      return {
        ...state,
        items: state.items.slice(
          0,
          Math.max(0, state.items.length - action.count),
        ),
      };
    case 'add-color':
    case 'remove-color':
    case 'change-color':
    case 'add-typography-style':
    case 'remove-typography-style':
    case 'change-typography-style':
      return state;
    case 'add-drafts': {
      return {
        ...state,
        draftItems: state.draftItems.concat(action.draftItems),
      };
    }
    case 'start-stream': {
      return {
        ...state,
        loading: {
          streaming: {
            conversationID: undefined,
            items: [],
            availableFidgetModules: [],
          },
        },
      };
    }
    case 'complete-stream': {
      if (state.loading?.streaming === undefined) {
        return state;
      }

      function getConversationID(): string | undefined {
        if (state.type === 'conversation') {
          return state.conversationID;
        }

        return state.loading?.streaming?.conversationID;
      }
      const conversationID = getConversationID();
      if (conversationID === undefined) {
        // Treat missing stream IDs as interrupted streams and preserve partial output.
        return conversationReducer(state, { type: 'interrupt-stream' });
      }

      const allNewItems = state.draftItems.concat(
        state.loading.streaming.items,
      );
      return conversationReducer(state, {
        type: 'received-conversation',
        conversationID,
        items: allNewItems,
      });
    }
    case 'stream-item': {
      if (state.loading?.streaming === undefined) {
        return state;
      }

      return {
        ...state,
        loading: {
          ...state.loading,
          streaming: {
            ...state.loading.streaming,
            items: state.loading.streaming.items.concat(action.item),
          },
        },
      };
    }
    case 'stream-fidget': {
      if (state.loading?.streaming === undefined) {
        return state;
      }

      return {
        ...state,
        loading: {
          ...state.loading,
          streaming: {
            ...state.loading.streaming,
            availableFidgetModules:
              state.loading.streaming.availableFidgetModules.concat(
                action.module,
              ),
          },
        },
      };
    }
    case 'stream-conversation-id': {
      if (state.loading?.streaming === undefined) {
        return state;
      }

      return {
        ...state,
        loading: {
          ...state.loading,
          streaming: {
            ...state.loading.streaming,
            conversationID: action.conversationID,
          },
        },
      };
    }
  }
}

function createSSEHandler({
  dispatch,
  stream,
}: {
  dispatch: ConversationDispatch;
  stream?: boolean;
}): SSEEventHandler | undefined {
  if (stream !== true) {
    return undefined;
  }
  return {
    onEvent: (event) => {
      const conversationEvent: ConversationEvent = JSON.parse(event.data);
      switch (conversationEvent.type) {
        case 'item':
          dispatch({
            type: 'stream-item',
            item: conversationEvent.conversationItem,
          });
          break;
        case 'fidget':
          dispatch({
            type: 'stream-fidget',
            module: conversationEvent.fidgetModule,
          });
          break;
        case 'started':
          dispatch({
            type: 'stream-conversation-id',
            conversationID: conversationEvent.conversationID,
          });
          break;
      }
    },
    onFinish: () => {
      dispatch({ type: 'complete-stream' });
    },
    onError: () => {
      dispatch({ type: 'interrupt-stream' });
    },
  };
}

export function ConversationBoundary({ children }: React.PropsWithChildren) {
  const { state: authenticationState, service: authService } =
    useAuthentication();
  const [state, _dispatch] = useReducer(
    conversationReducer,
    undefined,
    createInitialConversationState,
  );
  const hasHydratedRef = useRef(false);
  const immediatePublishQueueRef = useRef(Promise.resolve());

  const isAuthenticated = authenticationState.type === 'authenticated';
  const authToken = isAuthenticated ? authenticationState.token : undefined;
  const stateRef = useRef(state);
  const authServiceRef = useRef(authService);
  stateRef.current = state;
  authServiceRef.current = authService;

  async function startConversation({
    newItems,
    stream,
  }: {
    newItems: ConversationItem[];
    stream?: boolean;
  }): Promise<void> {
    try {
      const state = stateRef.current;
      const endpoint =
        stream === true ? 'conversation/start/stream' : 'conversation/start';

      const sseEventHandler = createSSEHandler({ dispatch, stream });
      if (stream === true) {
        dispatch({ type: 'start-stream' });
      }
      const payload = await authService.sendRequest({
        endpoint,
        method: 'POST',
        bodyObj: state.items.concat(state.draftItems).concat(newItems),
        requiresAuth: true,
        sseEventHandler,
      });

      if (payload === undefined) {
        return;
      }
      const { id: conversationID, items: receivedItems } = payload;
      const submittedItemCount =
        state.items.length + state.draftItems.length + newItems.length;
      dispatch({
        type: 'promote-to-conversation',
        conversationID,
        extraItems: receivedItems.slice(submittedItemCount),
        clearDraftItems: true,
      });
    } catch (error) {
      dispatch({ type: 'submit-failed' });
      throw error;
    }
  }

  async function updateShareStatus({
    sharedToPublic,
    conversationID,
  }: {
    sharedToPublic: boolean;
    conversationID: string;
  }): Promise<void> {
    try {
      const { sharedToPublic: serverSharedToPublic } =
        await authService.sendRequest({
          endpoint: 'conversation/share',
          method: 'POST',
          bodyObj: {
            conversationID,
            sharedToPublic,
          },
          requiresAuth: true,
        });
      dispatch({
        type: 'update-share-status',
        sharedToPublic: serverSharedToPublic,
        conversationID,
      });
    } catch {
      console.error('Failed to updateShareStatus');
    }
  }

  async function updateConversation({
    newItems,
    conversationID,
    stream,
  }: {
    newItems: ConversationItem[];
    conversationID: string;
    stream?: boolean;
  }): Promise<void> {
    try {
      const state = stateRef.current;
      const endpoint = stream === true ? 'conversation/stream' : 'conversation';
      const sseEventHandler = createSSEHandler({ dispatch, stream });
      if (stream === true) {
        dispatch({ type: 'start-stream' });
      }
      const payload = await authService.sendRequest({
        endpoint,
        method: 'POST',
        bodyObj: {
          id: conversationID,
          items: state.draftItems.concat(newItems),
        },
        requiresAuth: true,
        sseEventHandler,
      });

      if (payload === undefined) {
        return;
      }

      const { items: receivedItems } = payload;
      dispatch({
        type: 'received-conversation',
        conversationID,
        items: receivedItems,
      });
    } catch (error) {
      dispatch({ type: 'submit-failed' });
      throw error;
    }
  }

  const publishItemsImmediately = useCallback(
    ({ newItems }: { newItems: ConversationItem[] }) => {
      immediatePublishQueueRef.current = immediatePublishQueueRef.current.then(
        async () => {
          const state = stateRef.current;
          const authService = authServiceRef.current;

          _dispatch({ type: 'commit-items', items: newItems });

          try {
            switch (state.type) {
              case 'new': {
                const requestItems = state.items.concat(newItems);
                const payload = await authService.sendRequest({
                  endpoint: 'conversation/start',
                  method: 'POST',
                  bodyObj: requestItems,
                  requiresAuth: true,
                });

                if (payload === undefined) {
                  return;
                }

                _dispatch({
                  type: 'promote-to-conversation',
                  conversationID: payload.id,
                  extraItems: payload.items.slice(requestItems.length),
                  sharedToPublic: payload.sharedToPublic,
                });
                break;
              }
              case 'conversation': {
                await authService.sendRequest({
                  endpoint: 'conversation',
                  method: 'POST',
                  bodyObj: {
                    id: state.conversationID,
                    items: newItems,
                  },
                  requiresAuth: true,
                });
                break;
              }
            }
          } catch (error) {
            console.error(
              'Failed to publish conversation items immediately',
              error,
            );
            _dispatch({ type: 'rollback-items', count: newItems.length });
          }
        },
      );
    },
    [_dispatch],
  );

  const dispatch = useCallback<ConversationDispatch>(
    (action) => {
      const newItems = conversationActionToImmediateItems(action);
      if (newItems !== undefined) {
        publishItemsImmediately({ newItems });
        return;
      }

      _dispatch(action);
    },
    [_dispatch, publishItemsImmediately],
  );

  useEffect(() => {
    if (EnableCleanSlate) {
      // Don't do GET /conversations in EnableCleanSlate
      return;
    }

    if (!isAuthenticated) {
      hasHydratedRef.current = false;
      dispatch({ type: 'reset-conversation' });
      return;
    }

    if (hasHydratedRef.current) {
      return;
    }
    hasHydratedRef.current = true;

    void (async () => {
      dispatch({ type: 'reset-conversation' });
      dispatch({ type: 'start-load' });
      try {
        const conversations: ServerConversation[] =
          await authService.sendRequest({
            endpoint: 'conversations',
            method: 'GET',
            requiresAuth: true,
          });
        const lastConversation =
          conversations === undefined ? undefined : conversations[0];

        if (lastConversation === undefined) {
          dispatch({ type: 'finish-load' });
          return;
        }

        dispatch({
          type: 'hydrate-conversation',
          conversationID: lastConversation.id,
          items: lastConversation.items,
          sharedToPublic: lastConversation.sharedToPublic,
        });
      } catch (error) {
        console.error('Failed to fetch conversations', error);
        dispatch({ type: 'fail-load' });
      }
    })();
  }, [authService, authToken, dispatch, isAuthenticated]);

  const service: ConversationService = {
    sendItems: ({ newItems, stream }) => {
      void (async () => {
        await immediatePublishQueueRef.current;
        const state = stateRef.current;
        dispatch({ type: 'add-drafts', draftItems: newItems });
        dispatch({ type: 'start-load' });

        try {
          switch (state.type) {
            case 'new':
              await startConversation({ newItems, stream });
              break;
            case 'conversation':
              await updateConversation({
                newItems,
                conversationID: state.conversationID,
                stream,
              });
              break;
          }
        } catch (error) {
          console.error('Failed to send conversation items', error);
        }
      })();
    },
    startNewConversation: () => {
      dispatch({ type: 'reset-conversation' });
    },
    sharePublicly: () => {
      if (state.type !== 'conversation') {
        return;
      }

      if (state.loading !== undefined) {
        return;
      }

      void (async () => {
        await updateShareStatus({
          sharedToPublic: state.sharedToPublic === true ? false : true,
          conversationID: state.conversationID,
        });
      })();
    },
  };
  return (
    <ConversationContext.Provider value={{ service, state, dispatch }}>
      {children}
    </ConversationContext.Provider>
  );
}
